// shared-lib/group-customization/settleGroups.js
//
// The ONE call every commit site makes after a move or rotate: enforce the
// fixture-group links of every group that contains a moved object. Pins =
// the moved ids; the rest of each group follows its links. Links are read
// through `groupLinksIn` — the anchor's legacy list PLUS the room-level
// registry — and written back to whichever store each came from.
//
// A rigid group translation preserves every offset, so on the common path
// this returns the SAME room reference and costs one solve per group. Where
// it does move something, that is a link being honoured for the first time
// outside the Group Editor — and `resolveGroup`'s geometry-wins reconcile
// has already rewritten any never-verified offset before it can.
//
// Moved here from `web-app/src/utils/resolveGroupsAfterMove.js` (which
// re-exports it): a cabinet-run re-solve settles the groups it moved, and the
// server re-solves runs too (the customer's option prices).
//
// See reference-docs/layout-relationships/overview.md §3.2.

const { resolveGroup } = require('./resolveGroup');
const { pickAnchor } = require('./anchorTypes');
const { groupLinksIn } = require('./roomLinks');
const { fixedAxes } = require('./physicalAxes');
const { fixedNodesForLinks } = require('./fixedNodes');

/**
 * @param {Object} roomData — the room (room-store shape: `objects`, `walls`)
 * @param {Set<string>} movedIds — objects the caller just moved
 * @returns {{ room: Object, moved: string[], warnings: Object[] }} `room` is
 *   `roomData` itself when nothing changed.
 */
function resolveGroupsAfterMove(roomData, movedIds, opts = {}) {
  const objects = roomData?.objects || [];
  const none = { room: roomData, moved: [], warnings: [] };
  if (!objects.length || !movedIds?.size) return none;
  const roomLevelIds = new Set((roomData.links || []).map((l) => l?.id));

  const groupIds = new Set(
    objects
      .filter((o) => movedIds.has(o.id) && o.fixtureGroupId)
      .map((o) => o.fixtureGroupId)
  );
  if (!groupIds.size) return none;

  let next = objects;
  let nextRoomLinks = roomData.links || [];
  let changed = false;
  const moved = [];
  const warnings = [];

  for (const gid of groupIds) {
    const group = next.filter((o) => o.fixtureGroupId === gid);
    const anchor = pickAnchor(group);
    if (!anchor) continue;
    const links = groupLinksIn(
      { ...roomData, objects: next, links: nextRoomLinks },
      gid
    );
    if (!links.length) continue;

    const fixedNodes = fixedNodesForLinks(
      links,
      anchor,
      roomData,
      opts.structureOpenings
    );
    // Pins are the members the user moved — minus any member a link binds to
    // a FIXED node (a light centered on a window). The move group translates
    // rigidly, so that member arrived moved too, but that was a side effect
    // of `getMoveGroup`, not intent: the window wins and the member goes back
    // to it. It stays pinned only when it is the sole moved member (the
    // user dragged the light itself; the editor then reports the conflict).
    const movedHere = group.filter((o) => movedIds.has(o.id)).map((o) => o.id);
    const boundToGeometry = new Set();
    if (fixedNodes.size) {
      for (const l of links) {
        if (!(l?.fixedMembers?.length || l?.axis)) continue;
        for (const id of l.memberIds || []) boundToGeometry.add(id);
        if (l.objectAId) boundToGeometry.add(l.objectAId);
        if (l.objectBId) boundToGeometry.add(l.objectBId);
      }
    }
    // `opts.unpinIds` — members the caller wants to FOLLOW their links even
    // though they are the only thing that moved (authoring a center-on from
    // the panel: the subject must go to the window, not hold its spot).
    const unpin = opts.unpinIds || new Set();
    const free = movedHere.filter(
      (id) => !boundToGeometry.has(id) && !unpin.has(id)
    );
    const pinnedIds = new Set(
      free.length ? free : movedHere.filter((id) => !unpin.has(id))
    );
    // Nothing left to pin: the fixed nodes seed the solve on their own; only
    // without any do we hold the anchor.
    if (pinnedIds.size === 0 && fixedNodes.size === 0) pinnedIds.add(anchor.id);

    const res = resolveGroup(group, links, {
      pinnedIds,
      allowNoPins: true,
      walls: roomData.walls || [],
      physicalAxes: (c, all) => fixedAxes(c, anchor, all, roomData),
      fixedNodes,
    });
    warnings.push(...res.warnings);
    if (res.rejected) continue;

    const linksChanged =
      res.links !== links &&
      JSON.stringify(res.links) !== JSON.stringify(links);
    if (!res.moved.length && !linksChanged) continue;

    changed = true;
    moved.push(...res.moved);
    const byId = new Map(res.objects.map((o) => [o.id, o]));
    // Each link goes back to the store it came from: room-level by id, the
    // rest onto the anchor's legacy list.
    const legacyOut = res.links.filter((l) => !roomLevelIds.has(l.id));
    const roomOut = new Map(
      res.links.filter((l) => roomLevelIds.has(l.id)).map((l) => [l.id, l])
    );
    if (roomOut.size) {
      nextRoomLinks = nextRoomLinks.map((l) => roomOut.get(l.id) || l);
    }
    const legacyBefore = anchor.groupCustomization?.links || [];
    const legacyChanged =
      JSON.stringify(legacyOut) !== JSON.stringify(legacyBefore);
    next = next.map((o) => {
      let u = byId.get(o.id) || o;
      if (o.id === anchor.id && legacyChanged) {
        u = {
          ...u,
          // The anchor stored fact — stamped whenever the record is written.
          groupAnchor: true,
          groupCustomization: {
            ...(u.groupCustomization || {}),
            links: legacyOut,
          },
        };
      }
      return u;
    });
  }

  if (!changed) return { room: roomData, moved, warnings };
  const room = { ...roomData, objects: next };
  if (nextRoomLinks !== roomData.links) room.links = nextRoomLinks;
  return { room, moved, warnings };
}

module.exports = { resolveGroupsAfterMove };
